import { APP_BASE_HREF } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, Provider, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideNgxWebstorage, withLocalStorage, withSessionStorage } from 'ngx-webstorage';

import { AppShellModule, AppShellUtilitiesModule, Preloader } from '@priva/appshell';
import { PrivaAuthCurrentUserService } from '@priva/auth/current-user';
import { PrivaErrorPageHandlerService } from '@priva/error-pages';
import {
    PrivaLocalizationConfig,
    PrivaLocalizationOptions,
    providePrivaLocalization,
    VersionBasedTranslationLoader,
} from '@priva/localization';
import { PrivaNotificationsService } from '@priva/notifications';
import { PrivaPreferencesOptions, providePrivaPreferences } from '@priva/preferences/core';
import { PrivaToggleRouter } from '@priva/utilities/feature-toggles';
import { AppConfiguration } from '@priva/utilities/layout';
import { PrivaScrollbarModule } from '@priva/utilities/scrollbar';
import { PrivaScrollToModule } from '@priva/utilities/scrollto';
import { PrivaToggleModule } from '@priva/utilities/toggle';
import { PrivaWindowEventsModule } from '@priva/utilities/window-events';

import { HelpInfoModule } from 'app/core/components/help';
import { NavigationModule } from 'app/core/routing/navigation.module';
import { AppShellUserInfoService } from 'app/core/services';
import { ScanFlowStateModule } from 'app/features/scan-flow/state/scan-flow/scan-flow-state.module';
import { ScannedCodesStateModule } from 'app/features/scan-flow/state/scanned-codes/scanned-codes-state.module';
import { SettingsStateModule } from 'app/features/settings/state/settings/settings-state.module';
import { AppConfigurationModule, AppConfigurationService } from 'app/shared/configuration';
import { BatchStateModule } from 'app/shared/features/batch-selector/state/batch-state.module';
import { ActivityStateModule } from 'app/state/activities/activity-state.module';
import { ApiConfigStateModule } from 'app/state/api-config/api-config-state.module';
import { AppStateModule } from 'app/state/app-state/app-state.module';
import { BreakStateModule } from 'app/state/break/break-state.module';
import { ContainerStateModule } from 'app/state/containers/container-state.module';
import { EmployeeStateModule } from 'app/state/employee/employee-state.module';
import { GreenHouseMapStateModule } from 'app/state/green-house-map/green-house-map-state.module';
import { LocationsModule } from 'app/state/locations/locations.module';
import { ManualFlowStateModule } from 'app/state/manual-flow/manual-flow-state.module';
import { MasterDataStateModule } from 'app/state/master-data/master-data-state.module';
import { MultiUserPreferencesStateModule } from 'app/state/multi-user-preferences/multi-user-preferences-state.module';
import { ProductBoxStateModule } from 'app/state/product-boxes/product-box-state.module';
import { RegistrationStateModule } from 'app/state/registrations/registration-state.module';
import { RowSelectionStateModule } from 'app/state/row-selection/row-selection-state.module';
import { ServiceWorkerStateModule } from 'app/state/service-worker/service-worker-state.module';
import { UserSessionStateModule } from 'app/state/user-session/user-session-state.module';

import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { ResultRegistrationFlowStateModule } from './app/features/manual-input-result-registration/state/result-registration-flow-state.module';
import { ContainerMeasurementStateModule } from './app/state/container-measurements/container-measurement-state.module';
import { environment } from './environments/environment';

function getAppShellConfig(configService: AppConfigurationService): AppConfiguration {
    return AppConfiguration.from(configService.configuration);
}

function getCustomLocalizationOptions(): PrivaLocalizationOptions {
    const options = new PrivaLocalizationOptions();
    options.path = 'assets/translations';
    options.version = environment.version;
    options.availableLanguagesResource = 'assets/translations/locales.json';
    options.defaultLanguage = 'en-US';
    return options;
}

const localizationOptions: PrivaLocalizationConfig = {
    options: {
        provide: PrivaLocalizationOptions,
        useFactory: getCustomLocalizationOptions,
    },
};

const preferencesOptionsProvider: Provider = {
    provide: PrivaPreferencesOptions,
    deps: [AppConfigurationService],
    useFactory: (configService: AppConfigurationService): PrivaPreferencesOptions => {
        const { preferenceApi } = configService.configuration.uris;
        return { preferenceApi };
    },
};

function getVersionBasedTranslationLoader(
    http: HttpClient,
    options: PrivaLocalizationOptions,
): VersionBasedTranslationLoader {
    return new VersionBasedTranslationLoader(http, options);
}

/**
 * Preparation for State Management
 */
const STORE_CONFIG = {
    runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: true,
        strictActionSerializability: true,
    },
};

/* istanbul ignore next lvb: depends on environment */
const devImports = [
    StoreDevtoolsModule.instrument({
        maxAge: 250,
        logOnly: false,
    }),
];

if (environment.production) {
    enableProdMode();
}

/**
 * Feature Toggle Router
 */
export function getToggleRouter(configService: AppConfigurationService): PrivaToggleRouter {
    return new PrivaToggleRouter(configService.configuration.features || []);
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            BrowserModule,
            AppRoutingModule,
            NavigationModule.forRoot(),
            AppConfigurationModule.forRoot(),
            PrivaWindowEventsModule.forRoot(),
            PrivaScrollbarModule.forRoot(),
            AppShellModule.forRoot(
                {
                    provide: AppConfiguration,
                    useFactory: getAppShellConfig,
                    deps: [AppConfigurationService],
                },
                [
                    {
                        provide: PrivaToggleRouter,
                        useFactory: getToggleRouter,
                        deps: [AppConfigurationService],
                    },
                    NavigationModule.provideDefaultNavigation(),
                ],
            ),
            AppShellUtilitiesModule,
            PrivaToggleModule.forRoot(),
            PrivaScrollToModule.forRoot(),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: getVersionBasedTranslationLoader,
                    deps: [HttpClient, PrivaLocalizationOptions],
                },
            }),
            /**
             * StateManagement, NgRx Store & Effects
             */
            StoreModule.forRoot({}, { ...STORE_CONFIG }),
            EffectsModule.forRoot([]),
            ActivityStateModule,
            ApiConfigStateModule,
            AppStateModule,
            BatchStateModule,
            BreakStateModule,
            UserSessionStateModule,
            ContainerStateModule,
            EmployeeStateModule,
            GreenHouseMapStateModule,
            LocationsModule,
            MasterDataStateModule,
            MultiUserPreferencesStateModule,
            ProductBoxStateModule,
            ScannedCodesStateModule,
            RegistrationStateModule,
            ContainerMeasurementStateModule,
            RowSelectionStateModule,
            ScanFlowStateModule,
            ManualFlowStateModule,
            ResultRegistrationFlowStateModule,
            ServiceWorkerStateModule,
            SettingsStateModule,
            ServiceWorkerModule.register('ngsw-worker.js', {
                enabled: environment.production,
                registrationStrategy: 'registerImmediately',
            }),
            ...devImports,
            HelpInfoModule.forRoot(),
        ),
        { provide: PrivaAuthCurrentUserService, useExisting: AppShellUserInfoService },
        { provide: APP_BASE_HREF, useValue: '/' },
        PrivaErrorPageHandlerService,
        PrivaNotificationsService,
        provideHttpClient(withInterceptorsFromDi()),
        provideNgxWebstorage(withLocalStorage(), withSessionStorage()),
        provideAnimations(),
        provideZoneChangeDetection(),
        /**
         * Localization, Storage & Translate configuration
         */
        providePrivaLocalization(localizationOptions),
        providePrivaPreferences(preferencesOptionsProvider),
    ],
})
    .then(() => {
        // trigger preloader
        if (Preloader.appBootstrap) {
            Preloader.appBootstrap();
        }
    })
    /* eslint-disable-next-line */
    .catch((err) => console.log(err));
